function parseCompositeJson(wrapper, selector, fallback = []) {
  const script = wrapper.querySelector(selector);
  if (!script) return fallback;

  try {
    return JSON.parse(script.textContent);
  } catch (error) {
    console.error(`Failed to parse ${selector}`, error);
    return fallback;
  }
}

function isColorOption(optionName) {
  const normalizedOption = String(optionName || '').trim().toLowerCase();
  return normalizedOption === 'color' || normalizedOption === 'colour';
}

function getComponentSelectionLabel(optionNames, componentFieldsets) {
  if (!Array.isArray(optionNames)) return '';

  const selectedValues = optionNames
    .filter((optionName) => !isColorOption(optionName))
    .map((optionName) => {
      const fieldset = Array.from(componentFieldsets).find((item) => item.dataset.optionName === optionName);
      if (!fieldset) return '';

      const checkedInput = fieldset.querySelector('input:checked');
      return checkedInput ? checkedInput.value : '';
    })
    .filter(Boolean);

  return selectedValues.join(' / ');
}

function findVariantBySelections(variants, optionNames, selectedColor, componentFieldsets) {
  if (!Array.isArray(variants) || !Array.isArray(optionNames) || !selectedColor) return null;

  const expectedOptions = optionNames.map((optionName) => {
    if (isColorOption(optionName)) return selectedColor;

    const fieldset = Array.from(componentFieldsets).find((item) => item.dataset.optionName === optionName);
    if (!fieldset) return null;

    const checkedInput = fieldset.querySelector('input:checked');
    return checkedInput ? checkedInput.value : null;
  });

  if (expectedOptions.some((value) => value == null || value === '')) return null;

  return variants.find((variant) => {
    return Array.isArray(variant.options) && variant.options.every((value, index) => value === expectedOptions[index]);
  }) || null;
}

function setCompositeProperty(productForm, propertyName, value) {
  if (!productForm) return;

  const input = productForm.querySelector(`[data-composite-property="${propertyName}"]`);
  if (input) input.value = value || '';
}

function updateCompositeSetState(wrapper) {
  if (!wrapper) return;

  const sectionId = wrapper.dataset.section;
  const parentVariants = parseCompositeJson(wrapper, '[data-parent-variants]');
  if (!parentVariants.length) return;

  const colorInput = wrapper.querySelector('.js-radio-colors input:checked');
  if (!colorInput) return;

  const selectedColor = colorInput.value;
  const matchedParentVariant = parentVariants.find((variant) => {
    return Array.isArray(variant.options) && variant.options[0] === selectedColor;
  });

  const productForm = document.querySelector(`#product-form-${sectionId}`);
  if (productForm) {
    const variantInput = productForm.querySelector('input[name="id"]');
    if (variantInput && matchedParentVariant && variantInput.value !== String(matchedParentVariant.id)) {
      variantInput.value = matchedParentVariant.id;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  const topVariants = parseCompositeJson(wrapper, '[data-top-product-variants]');
  const topOptionNames = parseCompositeJson(wrapper, '[data-top-product-options]');
  const bottomVariants = parseCompositeJson(wrapper, '[data-bottom-product-variants]');
  const bottomOptionNames = parseCompositeJson(wrapper, '[data-bottom-product-options]');

  const topFieldsets = wrapper.querySelectorAll('[data-component="top"]');
  const bottomFieldsets = wrapper.querySelectorAll('[data-component="bottom"]');
  const topVariant = findVariantBySelections(topVariants, topOptionNames, selectedColor, topFieldsets);
  const bottomVariant = findVariantBySelections(bottomVariants, bottomOptionNames, selectedColor, bottomFieldsets);
  const topSelectionLabel = getComponentSelectionLabel(topOptionNames, topFieldsets);
  const bottomSelectionLabel = getComponentSelectionLabel(bottomOptionNames, bottomFieldsets);

  setCompositeProperty(productForm, 'color', selectedColor);
  setCompositeProperty(productForm, 'top-size', topSelectionLabel);
  setCompositeProperty(productForm, 'bottom-size', bottomSelectionLabel);
  setCompositeProperty(productForm, 'top-variant-id', topVariant ? String(topVariant.id) : '');
  setCompositeProperty(productForm, 'bottom-variant-id', bottomVariant ? String(bottomVariant.id) : '');

  updateCompositeSubmitState(wrapper, productForm, {
    selectedColor,
    matchedParentVariant,
    topVariant,
    bottomVariant,
    topFieldsets,
    bottomFieldsets,
  });
}

function updateCompositeSubmitState(wrapper, productForm, state) {
  if (!productForm) return;

  const submitButton = productForm.querySelector('[type="submit"]');
  const variantInput = productForm.querySelector('input[name="id"]');
  if (!submitButton || !variantInput) return;

  const topRequired = state.topFieldsets.length > 0;
  const bottomRequired = state.bottomFieldsets.length > 0;
  const topSelected = !topRequired || getComponentSelectionLabel(
    parseCompositeJson(wrapper, '[data-top-product-options]'),
    state.topFieldsets
  );
  const bottomSelected = !bottomRequired || getComponentSelectionLabel(
    parseCompositeJson(wrapper, '[data-bottom-product-options]'),
    state.bottomFieldsets
  );

  const parentReady =
    state.selectedColor &&
    state.matchedParentVariant &&
    state.matchedParentVariant.available !== false;
  const componentsReady =
    (!topRequired || (topSelected && state.topVariant?.available !== false)) &&
    (!bottomRequired || (bottomSelected && state.bottomVariant?.available !== false));

  const isReady = Boolean(parentReady && componentsReady);

  submitButton.disabled = !isReady;
  variantInput.disabled = !isReady;

  if (isReady) {
    submitButton.removeAttribute('aria-disabled');
  } else {
    submitButton.setAttribute('aria-disabled', 'true');
  }
}

document.addEventListener('change', (event) => {
  const wrapper = event.target.closest('.composite-set-picker-wrapper');
  if (!wrapper) return;

  updateCompositeSetState(wrapper);
});

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.composite-set-picker-wrapper').forEach((wrapper) => {
    updateCompositeSetState(wrapper);
  });
});
